// src/Views/HomePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import AdvertisePopup from '../Components/AdvertisePopup';
import { useAuth } from "@clerk/react";
import useSavedCars from '../hooks/useSavedCars';
import usePaidPlan from '../hooks/usePaidPlan';
import './HomePage.css';
import '../Views/CarPage.css';

// Fisher-Yates (Knuth) shuffle algorithm
const shuffleArray = (array) => {
    const shuffled = [...array]; // Create a copy to avoid mutating the original
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
    }
    return shuffled;
};

const HomePage = () => {
    const [modelLines, setModelLines] = useState([]);
    const [advertisers, setAdvertisers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { userId } = useAuth();
    // Check if user has the bidder_iq plan via Clerk Billing OR user metadata
    const { hasPaidPlan } = usePaidPlan();
    const { isSaved, toggleSavedCar } = useSavedCars(hasPaidPlan ? userId : null);
    const [vinResults, setVinResults] = useState([]);
    const [vinModelData, setVinModelData] = useState(null);
    const [vinSearching, setVinSearching] = useState(false);
    const [liveAuctionModelIds, setLiveAuctionModelIds] = useState(new Set());

    // Detect if search term looks like a VIN
    const isVinSearch = (term) => {
        if (!term || term.trim().length < 5) return false;
        const cleaned = term.trim();
        // Standard 17-char VIN (post-1981)
        if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(cleaned)) return true;
        // Pre-1985 shorter numeric/alphanumeric VINs (6+ chars, mostly digits)
        if (cleaned.length >= 6 && cleaned.length < 17 && /^[A-Z0-9]+$/i.test(cleaned) && /\d{4,}/.test(cleaned)) return true;
        return false;
    };

    // Search for VIN in auctions collection
    useEffect(() => {
        const searchVin = async () => {
            const trimmed = searchTerm.trim();
            if (!isVinSearch(trimmed)) {
                setVinResults([]);
                setVinModelData(null);
                return;
            }

            setVinSearching(true);
            try {
                const auctionsRef = collection(db, 'auctions');
                const q = query(auctionsRef, where('vin', '==', trimmed));
                const snapshot = await getDocs(q);
                const auctions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setVinResults(auctions);

                // Fetch corresponding model data if we have results
                if (auctions.length > 0 && auctions[0].modelId) {
                    const modelQuery = query(collection(db, 'modelLines'), where('modelId', '==', auctions[0].modelId));
                    const modelSnapshot = await getDocs(modelQuery);
                    if (!modelSnapshot.empty) {
                        setVinModelData({ id: modelSnapshot.docs[0].id, ...modelSnapshot.docs[0].data() });
                    } else {
                        setVinModelData(null);
                    }
                } else {
                    setVinModelData(null);
                }
            } catch (error) {
                console.error('Error searching VIN:', error);
                setVinResults([]);
                setVinModelData(null);
            } finally {
                setVinSearching(false);
            }
        };

        const debounce = setTimeout(searchVin, 400);
        return () => clearTimeout(debounce);
    }, [searchTerm]);

    const formatMileage = (miles) => {
        if (!miles) return '0';
        const numericValue = miles.toString().replace(/[^0-9]/g, '');
        return numericValue.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',');
    };

    const handleTileClick = (modelId, e) => {
        e.preventDefault();
        navigate(`/car/${modelId}`);
    };

    useEffect(() => {
        if (!hasPaidPlan) return;
        const fetchLiveAuctions = async () => {
            try {
                const auctionsRef = collection(db, 'auctions');
                const q = query(auctionsRef, where('finalBid', '==', null));
                const snapshot = await getDocs(q);
                const modelIds = new Set(snapshot.docs.map(doc => doc.data().modelId).filter(Boolean));
                setLiveAuctionModelIds(modelIds);
            } catch (error) {
                console.error('Error fetching live auctions:', error);
            }
        };
        fetchLiveAuctions();
    }, [hasPaidPlan]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch model lines
                const modelLinesRef = collection(db, 'modelLines');
                const modelSnapshot = await getDocs(modelLinesRef);
                const models = modelSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setModelLines(shuffleArray(models));

                // Fetch advertisers
                const advertisersRef = collection(db, 'advertisers');
                const advertiserSnapshot = await getDocs(advertisersRef);
                const ads = advertiserSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setAdvertisers(shuffleArray(ads));

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Function to normalize text for fuzzy matching (removes spaces, special chars)
    const normalizeText = (text) => {
        if (!text) return '';
        return text.toLowerCase().replace(/[\s\-\(\)\.]/g, '');
    };

    // Function to extract model variant by combining modelLine number with model's first letter
    // e.g., modelLine="911 (LWB 2.4)" + model="S Coupe" -> "911s"
    const extractModelVariant = (modelLine, modelName) => {
        if (!modelLine) return '';
        
        // Extract the base number from modelLine (e.g., "911" from "911 (LWB 2.4)")
        const baseMatch = modelLine.match(/^(\d+)/);
        if (!baseMatch) return '';
        const baseNum = baseMatch[1];
        
        // Extract the first letter/word from model name (e.g., "S" from "S Coupe")
        if (modelName) {
            const variantMatch = modelName.match(/^([A-Za-z]+)/);
            if (variantMatch) {
                const variant = variantMatch[1].toLowerCase();
                // Only use single letter variants (S, T, E, R, etc.)
                if (variant.length === 1) {
                    return baseNum + variant;
                }
            }
        }
        return baseNum;
    };

    // Function to check if a model matches the search term
    const isModelMatch = (model, term) => {
        if (!term.trim()) return true; // Show all if search is empty
        
        // Create normalized versions for fuzzy matching
        const normalizedModelLine = normalizeText(model.modelLine);
        const normalizedModel = normalizeText(model.model);
        const normalizedModelCombined = normalizedModelLine + normalizedModel;
        const modelVariant = extractModelVariant(model.modelLine, model.model);
        
        console.log('modelLine:', model.modelLine, '| model:', model.model, '-> variant:', modelVariant);
        
        // Split search term into individual words
        const searchTerms = term.toLowerCase().split(/\s+/).filter(t => t.length > 0);
        
        // Check if ALL search terms match the model
        const allTermsMatch = searchTerms.every(searchTerm => {
            const normalizedSearchTerm = normalizeText(searchTerm);
            
            // Check model variant (handles "911s" matching "911 (LWB 2.4) S")
            if (modelVariant && modelVariant.includes(normalizedSearchTerm)) return true;
            
            // Check normalized model
            if (normalizedModelCombined.includes(normalizedSearchTerm)) return true;
            if (normalizedModelLine.includes(normalizedSearchTerm)) return true;
            if (normalizedModel.includes(normalizedSearchTerm)) return true;
            
            // Check for year matches
            if (model.year) {
                // Direct match in year string
                if (model.year.toLowerCase().includes(searchTerm)) return true;
                
                // Handle year ranges like "1972-1973" or "(1972-1973)"
                const yearClean = model.year.replace(/[()]/g, '');
                if (yearClean.includes('-')) {
                    const [startYear, endYear] = yearClean.split('-');
                    
                    // Handle open-ended ranges like "2023-"
                    if (endYear.trim() === '') {
                        const start = parseInt(startYear.trim());
                        const searchYear = parseInt(searchTerm);
                        
                        if (!isNaN(searchYear) && searchYear >= start) {
                            return true;
                        }
                    } else {
                        // Handle closed ranges like "1972-1973"
                        const start = parseInt(startYear.trim());
                        const end = parseInt(endYear.trim());
                        const searchYear = parseInt(searchTerm);
                        
                        if (!isNaN(searchYear) && searchYear >= start && searchYear <= end) {
                            return true;
                        }
                    }
                }
            }
            
            return false;
        });
        
        return allTermsMatch;
    };

    // Filter models based on search term, then sort saved cars first
    const filteredModels = modelLines
        .filter(model => isModelMatch(model, searchTerm))
        .sort((a, b) => {
            const aSaved = isSaved(a.modelId) ? 1 : 0;
            const bSaved = isSaved(b.modelId) ? 1 : 0;
            return bSaved - aSaved;
        });

    // Build grid items with advertisers inserted randomly per row
    // Only users with paid plan (bidder_iq) get ad-free experience
    const gridItems = useMemo(() => {
        // If user has paid plan, show only models without ads
        if (hasPaidPlan) {
            return filteredModels.map(model => ({ 
                type: 'model', 
                data: model, 
                key: model.modelId 
            }));
        }

        // For users without paid plan (including signed-in users), insert advertisers
        const items = [];
        const columnsPerRow = 5;
        const modelsPerRow = columnsPerRow - 1; // 4 models + 1 advertiser per row
        let advertiserIndex = 0;

        for (let i = 0; i < filteredModels.length; i += modelsPerRow) {
            const rowModels = filteredModels.slice(i, i + modelsPerRow);
            
            // Get a random position for the advertiser in this row (0 to columnsPerRow-1)
            const adPosition = Math.floor(Math.random() * columnsPerRow);
            
            // Get the advertiser for this row (cycle through if needed)
            const advertiser = advertisers.length > 0 
                ? advertisers[advertiserIndex % advertisers.length] 
                : null;
            advertiserIndex++;

            let modelIdx = 0;
            for (let col = 0; col < columnsPerRow; col++) {
                if (col === adPosition && advertiser) {
                    items.push({ type: 'advertiser', data: advertiser, key: `ad-${i}-${advertiser.id}` });
                } else if (modelIdx < rowModels.length) {
                    items.push({ type: 'model', data: rowModels[modelIdx], key: rowModels[modelIdx].modelId });
                    modelIdx++;
                }
            }
        }
        return items;
    }, [filteredModels, advertisers, hasPaidPlan]);

    if (loading) {
        return (
            <div className="betasml-homepage">
                <Header />
                <div className="loading">Loading model data...</div>
            </div>
        );
    }

    return (
        <div className="betasml-homepage">
            <Header 
                searchTerm={searchTerm}
                onSearchChange={(e) => setSearchTerm(e.target.value)}
                showSearch={true}
            />
            
            {isVinSearch(searchTerm.trim()) ? (
                vinSearching ? (
                    <div className="loading">Searching VIN...</div>
                ) : vinResults.length > 0 ? (
                    <div className="model-grid">
                            {vinModelData && (
                                <div 
                                    className="model-tile"
                                    onClick={(e) => handleTileClick(vinModelData.modelId, e)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="model-image-container">
                                        <img 
                                            src={vinModelData.imageLink} 
                                            alt={`${vinModelData.modelLine} ${vinModelData.model}`}
                                            className="model-image"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="model-info">
                                        <h3>{vinModelData.modelLine} {vinModelData.model}</h3>
                                        <p>({vinModelData.year})</p>
                                    </div>
                                    {hasPaidPlan && (
                                        <button 
                                            className="save-star-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSavedCar(vinModelData.modelId);
                                            }}
                                            aria-label={isSaved(vinModelData.modelId) ? 'Unsave car' : 'Save car'}
                                        >
                                            {isSaved(vinModelData.modelId) ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#32C5FF" stroke="#32C5FF" strokeWidth="2">
                                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#99E2FF" strokeWidth="1.5">
                                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>
                            )}
                            {vinResults.map((auction, index) => (
                                <a 
                                    key={index} 
                                    href={auction.auctionLink || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="model-tile vin-auction-tile"
                                    style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                >
                                    <div className="model-image-container">
                                        <img 
                                            src={auction.imageLink || vinModelData?.imageLink} 
                                            alt={auction.title || 'Auction'} 
                                            className="model-image"
                                            style={{ objectFit: 'cover' }}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="model-info">
                                        <h3>{auction.title || (vinModelData ? `${vinModelData.modelLine} ${vinModelData.model}` : 'Auction')}</h3>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                                        <span>{auction.miles ? `${formatMileage(auction.miles)} miles` : 'N/A'}</span>
                                        <span>
                                            {auction.auctionDate?.seconds ? 
                                                new Date(auction.auctionDate.seconds * 1000).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })
                                                : ''
                                            }
                                        </span>
                                    </div>
                                    {auction.finalBid == null || auction.finalBid === '' ? (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '14px', color: '#666' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#32C5FF' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#32C5FF', display: 'inline-block' }}></span>
                                                Live
                                            </span>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <span>{auction.auctionHouse}</span>
                                            {auction.auctionLink && (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                            </span>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span className={`status-dot ${auction.sold ? 'sold' : 'unsold'}`}></span>
                                                <span>{auction.sold ? 'Sold' : 'Unsold'}</span>
                                            </div>
                                            <span>${auction.finalBid?.toLocaleString()}</span>
                                        </div>
                                    )}
                                </a>
                            ))}
                    </div>
                ) : (
                    <div className="no-results model-grid">No auction results found for VIN "{searchTerm.trim()}"</div>
                )
            ) : (
            <div className="model-grid">
                {gridItems.length > 0 ? (
                    gridItems.map((item) => (
                        item.type === 'model' ? (
                            <div 
                                key={item.key} 
                                className={`model-tile${hasPaidPlan && liveAuctionModelIds.has(item.data.modelId) ? ' live-auction-glow' : ''}`}
                                onClick={(e) => handleTileClick(item.data.modelId, e)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="model-image-container">
                                    <img 
                                        src={item.data.imageLink} 
                                        alt={`${item.data.modelLine} ${item.data.model}`}
                                        className="model-image"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="model-info">
                                    <h3>{item.data.modelLine} {item.data.model}</h3>
                                    <p>({item.data.year})</p>
                                </div>
                                {hasPaidPlan && (
                                    <button 
                                        className="save-star-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSavedCar(item.data.modelId);
                                        }}
                                        aria-label={isSaved(item.data.modelId) ? 'Unsave car' : 'Save car'}
                                    >
                                        {isSaved(item.data.modelId) ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#32C5FF" stroke="#32C5FF" strokeWidth="2">
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#99E2FF" strokeWidth="1.5">
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <a 
                                key={item.key} 
                                className="advertiser-tile"
                                href={item.data.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="advertiser-logo-container">
                                    <img 
                                        src={item.data.logo} 
                                        alt={item.data.companyName}
                                        className="advertiser-logo"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="advertiser-info">
                                    <h3>{item.data.companyName}</h3>
                                    <p>{item.data.copy}</p>
                                </div>
                            </a>
                        )
                    ))
                ) : (
                    <div className="no-results">No models found matching "{searchTerm}"</div>
                )}
            </div>
            )}
            {!hasPaidPlan && <AdvertisePopup />}
            <Footer />
        </div>
    );
};

export default HomePage;
